import React, { useEffect, useRef, useState } from 'react'
import type { IndividualElement } from '@recurly/recurly-js'
import cx from 'classnames'
import { Message } from 'intl'
import { useUniqueId } from 'hooks'
import { devicePx } from 'helpers'

import { InputError } from 'components/inputs'

import messages from './messages'
import s from './RecurlyInput.module.css'


// Element state from recurly
type ElementState = {
  empty?: boolean
  valid: boolean
  focus?: boolean
}

type RecurlyInputProps = {
  className?: string
  label: Intl.Message
  element: IndividualElement
  size: 48 | 56
  'data-testid': string
  onBlur?: () => void
}

const RecurlyInput: React.FunctionComponent<RecurlyInputProps> = (props) => {
  const { className, label, element, size, 'data-testid': dataTestId, onBlur } = props

  const [ isFocused, setFocusedState ] = useState(false)
  const [ isFilled, setFilledState ] = useState(false)
  const [ error, setError ] = useState<string>(null)

  const inputRef = useRef()
  const controlId = useUniqueId('recurly-element')
  const onBlurRef = useRef(onBlur)
  onBlurRef.current = onBlur

  useEffect(() => {
    const paddings = {
      48: `${devicePx(19)}px ${devicePx(16)}px ${devicePx(5)}px`,
      56: `${devicePx(21)}px ${devicePx(16)}px ${devicePx(13)}px`,
    }

    element.configure({
      style: {
        fontFamily: window.getComputedStyle(inputRef.current).getPropertyValue('font-family'),
        fontSize: `${devicePx(16)}px`,
        fontColor: '#141414',
        fontWeight: 'normal',
        lineHeight: `${devicePx(22)}px`,
        padding: paddings[size],
        // @ts-ignore
        fontSmoothing: 'antialiased',
        textRendering: 'optimizeLegibility',
      },
    })
  }, [ element, size ])

  useEffect(() => {
    const handleChange = (state: ElementState) => {
      setFilledState(!state.empty)
    }

    const handleFocus = () => {
      setFocusedState(true)
    }

    const handleBlur = () => {
      setFocusedState(false)

      // if field is filled we can check an error
      // @ts-ignore
      if (!element.state.empty && !element.state.valid) {
        setError('INVALID')
      }
      else {
        // reset error
        setError(null)
      }

      if (typeof onBlurRef.current === 'function') {
        onBlurRef.current()
      }
    }

    const handleError = (error?: string) => {
      setError(error)
    }

    element.attach(inputRef.current)
    element.on('change', handleChange)
    element.on('focus', handleFocus)
    element.on('blur', handleBlur)
    // custom event for setting an error
    // @ts-ignore
    element.on('error', handleError)

    return () => {
      element.off('change', handleChange)
      element.off('focus', handleFocus)
      element.off('blur', handleBlur)
      // custom event for setting an error
      // @ts-ignore
      element.off('error', handleError)
      element.remove()
    }
  }, [ element ])

  const rootClassName = cx(className, s.root, s[`size-${size}`], {
    [s.focused]: isFocused,
    [s.errored]: error,
    [s.filled]: isFilled,
  })

  return (
    <div className={rootClassName} data-testid={dataTestId}>
      <div className="relative">
        <label className={s.label} htmlFor={controlId}>
          <Message value={label} />
        </label>
        <div id={controlId} ref={inputRef} className={s.control} />
      </div>
      {
        Boolean(error) && (
          <InputError
            message={error !== 'INVALID' ? error : messages.error}
            data-testid={`${dataTestId}Error`}
          />
        )
      }
    </div>
  )
}

export default RecurlyInput
